import { signal } from '@angular/core';

export interface PersistentDrawerTabsConfig {
  storageKey: string;
  channelName: string;
}

export interface PersistentDrawerTab<TType extends string = string> {
  id: string;
  type: TType;
  title: string;
  icon: string;
  context: Record<string, unknown>;
}

export class SharedPersistentDrawerTabsService<TType extends string = string> {
  private readonly _senderId = `${Date.now()}-${Math.random()}`;
  private _broadcastChannel: BroadcastChannel | null = null;

  tabs = signal<PersistentDrawerTab<TType>[]>([]);
  activeTabId = signal<string | null>(null);

  constructor(private readonly _config: PersistentDrawerTabsConfig) {
    this._hydrate();
    this._attachBroadcastSync();
    this._attachStorageSync();
  }

  openOrFocusTab(tab: PersistentDrawerTab<TType>): void {
    const existing = this.tabs().some((t) => t.id === tab.id);
    if (existing) {
      this.tabs.set(this.tabs().map((t) => (t.id === tab.id ? { ...t, ...tab } : t)));
    } else {
      this.tabs.set([...this.tabs(), tab]);
    }

    this.activeTabId.set(tab.id);
    this._persist();
  }

  setActiveTab(tabId: string | null): void {
    this.activeTabId.set(tabId);
    this._persist();
  }

  removeTab(tabId: string): void {
    this.tabs.set(this.tabs().filter((tab) => tab.id !== tabId));
    if (this.activeTabId() === tabId) {
      this.activeTabId.set(null);
    }
    this._persist();
  }

  updateTabContext(tabId: string, context: Record<string, unknown>): void {
    this.tabs.set(
      this.tabs().map((tab) =>
        tab.id === tabId
          ? {
              ...tab,
              context: {
                ...tab.context,
                ...context,
              },
            }
          : tab
      )
    );
    this._persist();
    this._broadcastContextUpdate(tabId, context);
  }

  private _hydrate(): void {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      const raw = window.localStorage.getItem(this._config.storageKey);
      if (!raw) {
        return;
      }

      const parsed = JSON.parse(raw) as {
        tabs?: PersistentDrawerTab<TType>[];
        activeTabId?: string | null;
      };
      this.tabs.set(parsed.tabs || []);
      this.activeTabId.set(null);
    } catch {
      this.tabs.set([]);
      this.activeTabId.set(null);
    }
  }

  private _persist(): void {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      window.localStorage.setItem(
        this._config.storageKey,
        JSON.stringify({
          tabs: this.tabs(),
          activeTabId: this.activeTabId(),
        })
      );
    } catch {
      // Ignore storage failures.
    }
  }

  private _attachBroadcastSync(): void {
    if (typeof window === 'undefined' || typeof BroadcastChannel === 'undefined') {
      return;
    }

    this._broadcastChannel = new BroadcastChannel(this._config.channelName);
    this._broadcastChannel.addEventListener('message', (event: MessageEvent) => {
      const payload = event.data as
        | {
            type?: 'context-update';
            senderId?: string;
            tabId?: string;
            context?: Record<string, unknown>;
          }
        | undefined;
      if (!payload || payload.senderId === this._senderId) {
        return;
      }

      if (payload.type !== 'context-update' || !payload.tabId || !payload.context) {
        return;
      }

      this.tabs.set(
        this.tabs().map((tab) =>
          tab.id === payload.tabId
            ? {
                ...tab,
                context: {
                  ...tab.context,
                  ...payload.context,
                },
              }
            : tab
        )
      );
    });
  }

  private _broadcastContextUpdate(tabId: string, context: Record<string, unknown>): void {
    if (!this._broadcastChannel) {
      return;
    }

    this._broadcastChannel.postMessage({
      type: 'context-update',
      senderId: this._senderId,
      tabId,
      context,
    });
  }

  private _attachStorageSync(): void {
    if (typeof window === 'undefined') {
      return;
    }

    window.addEventListener('storage', (event: StorageEvent) => {
      if (event.key !== this._config.storageKey || !event.newValue) {
        return;
      }

      try {
        const parsed = JSON.parse(event.newValue) as {
          tabs?: PersistentDrawerTab<TType>[];
          activeTabId?: string | null;
        };
        this._applyExternalState(parsed.tabs || []);
      } catch {
        // Ignore invalid external data.
      }
    });
  }

  private _applyExternalState(externalTabs: PersistentDrawerTab<TType>[]): void {
    this.tabs.set(externalTabs);

    // Keep focus local to this browser tab; only clear if the focused tab was removed.
    const currentActiveTabId = this.activeTabId();
    if (currentActiveTabId && !externalTabs.some((tab) => tab.id === currentActiveTabId)) {
      this.activeTabId.set(null);
    }
  }
}
